<template>
  <div class="bg-gradient-to-r from-content-800 to-content-700 rounded-lg p-4 mb-6">
    <!-- Mobile Filter Toggle -->
    <div class="md:hidden mb-4">
      <button
        @click="showMobileFilters = !showMobileFilters"
        class="w-full flex items-center justify-between px-4 py-2 bg-content-600 rounded-lg button-interactive"
      >
        <span class="font-medium text-white">Filtros</span>
        <svg 
          class="w-5 h-5 text-content-300 transform transition-transform smooth"
          :class="{ 'rotate-180': showMobileFilters }"
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m19 9-7 7-7-7"></path>
        </svg>
      </button>
    </div>

    <!-- Filters Content -->
    <div 
      class="space-y-4 md:space-y-0 md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-4"
      :class="{ 'hidden': !showMobileFilters && isMobile }"
    >
      <!-- Search -->
      <div>
        <label class="block text-sm font-medium text-white mb-2">
          Buscar
        </label>
        <input
          v-model="localFilters.search"
          type="text"
          placeholder="Buscar oportunidades..."
          class="border py-2 px-3 rounded-lg outline-none smooth focus:border-primary-500 smooth w-full bg-transparent"
          @input="debounceSearch"
        />
      </div>

      <!-- Instrument Type -->
      <div>
        <label class="block text-sm font-medium text-white mb-2">
          Tipo de instrumento
        </label>
        <select
          v-model="localFilters.instrument"
          class="border py-2 px-3 rounded-lg outline-none smooth focus:border-primary-500 smooth w-full bg-transparent"
          @change="applyFilters"
        >
          <option value="">Todos</option>
          <option value="Debt">Deuda</option>
          <option value="Co-ownership">Adquisición de inmueble</option>
        </select>
      </div>

      <!-- Investment Range -->
      <div>
        <label class="block text-sm font-medium text-white mb-2">
          Inversión mínima
        </label>
        <select
          v-model="localFilters.investmentRange"
          class="border py-2 px-3 rounded-lg outline-none smooth focus:border-primary-500 smooth w-full bg-transparent"
          @change="applyFilters"
        >
          <option value="">Cualquier monto</option>
          <option value="0-50000">Hasta $50,000</option>
          <option value="50000-100000">$50,000 - $100,000</option>
          <option value="100000-500000">$100,000 - $500,000</option>
          <option value="500000-">Más de $500,000</option>
        </select>
      </div>

      <!-- Sort -->
      <div>
        <label class="block text-sm font-medium text-white mb-2">
          Ordenar por
        </label>
        <select
          v-model="localFilters.sortBy"
          class="border py-2 px-3 rounded-lg outline-none smooth focus:border-primary-500 smooth w-full bg-transparent"
          @change="applyFilters"
        >
          <option value="newest">Más recientes</option>
          <option value="highestRate">Mayor rendimiento</option>
          <option value="shortestTerm">Menor plazo</option>
          <option value="lowestInvestment">Menor inversión</option>
          <option value="mostFunded">Más financiado</option>
        </select>
      </div>
    </div>

    <!-- Advanced Filters (Collapsible) -->
    <div class="mt-4 pt-4 border-t border-gray-200">
      <button
        @click="showAdvancedFilters = !showAdvancedFilters"
        class="flex items-center text-sm text-primary-600 hover:text-primary-700"
      >
        <span>Filtros avanzados</span>
        <svg 
          class="w-4 h-4 ml-1 transform transition-transform"
          :class="{ 'rotate-180': showAdvancedFilters }"
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m19 9-7 7-7-7"></path>
        </svg>
      </button>

      <div 
        v-show="showAdvancedFilters"
        class="mt-4 space-y-4 md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-4 md:space-y-0"
      >
        <!-- Rate Range -->
        <div>
          <label class="block text-sm font-medium text-white mb-2">
            Tasa anual (%)
          </label>
          <div class="flex space-x-2">
            <input
              v-model.number="localFilters.minRate"
              type="number"
              placeholder="Min"
              class="border py-2 px-3 rounded-lg outline-none smooth focus:border-primary-500 smooth flex-1 bg-transparent"
              @input="debounceFilters"
            />
            <input
              v-model.number="localFilters.maxRate"
              type="number"
              placeholder="Max"
              class="border py-2 px-3 rounded-lg outline-none smooth focus:border-primary-500 smooth flex-1 bg-transparent"
              @input="debounceFilters"
            />
          </div>
        </div>

        <!-- Term -->
        <div>
          <label class="block text-sm font-medium text-white mb-2">
            Plazo máximo
          </label>
          <select
            v-model="localFilters.maxTerm"
            class="border py-2 px-3 rounded-lg outline-none smooth focus:border-primary-500 smooth w-full bg-transparent"
            @change="applyFilters"
          >
            <option value="">Sin límite</option>
            <option value="12">Hasta 12 meses</option>
            <option value="24">Hasta 24 meses</option>
            <option value="36">Hasta 36 meses</option>
          </select>
        </div>

        <!-- Payment Frequency -->
        <div>
          <label class="block text-sm font-medium text-white mb-2">
            Pago de intereses
          </label>
          <select
            v-model="localFilters.interestPayment"
            class="border py-2 px-3 rounded-lg outline-none smooth focus:border-primary-500 smooth w-full bg-transparent"
            @change="applyFilters"
          >
            <option value="">Cualquier frecuencia</option>
            <option value="Mensual">Mensual</option>
            <option value="Trimestral">Trimestral</option>
            <option value="Semestral">Semestral</option>
            <option value="Anual">Anual</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Clear Filters -->
    <div v-if="hasActiveFilters" class="mt-4 pt-4 border-t border-gray-200">
      <button
        @click="clearFilters"
        class="text-sm text-gray-500 hover:text-gray-700 flex items-center"
      >
        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
        Limpiar filtros
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'OpportunityFilters',
  props: {
    filters: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['filter-change'],
  data() {
    return {
      showMobileFilters: false,
      showAdvancedFilters: false,
      localFilters: {
        search: '',
        instrument: '',
        investmentRange: '',
        sortBy: 'newest',
        minRate: null,
        maxRate: null,
        maxTerm: '',
        interestPayment: ''
      },
      searchTimeout: null,
      filterTimeout: null
    }
  },
  computed: {
    isMobile() {
      return window.innerWidth < 768;
    },
    hasActiveFilters() {
      return Object.values(this.localFilters).some(value => 
        value !== '' && value !== null && value !== undefined
      );
    }
  },
  watch: {
    filters: {
      handler(newFilters) {
        this.localFilters = { ...this.localFilters, ...newFilters };
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    this.checkMobileView();
    window.addEventListener('resize', this.checkMobileView);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobileView);
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    if (this.filterTimeout) clearTimeout(this.filterTimeout);
  },
  methods: {
    checkMobileView() {
      if (!this.isMobile) {
        this.showMobileFilters = true;
      }
    },
    debounceSearch() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.applyFilters();
      }, 300);
    },
    debounceFilters() {
      if (this.filterTimeout) clearTimeout(this.filterTimeout);
      this.filterTimeout = setTimeout(() => {
        this.applyFilters();
      }, 500);
    },
    applyFilters() {
      this.$emit('filter-change', { ...this.localFilters });
    },
    clearFilters() {
      this.localFilters = {
        search: '',
        instrument: '',
        investmentRange: '',
        sortBy: 'newest',
        minRate: null,
        maxRate: null,
        maxTerm: '',
        interestPayment: ''
      };
      this.applyFilters();
    }
  }
}
</script>